import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import useStyles from "./employee-requests-card.style";
import checkAll from "@iconify/icons-mdi/check-all";
import { InlineIcon } from "@iconify/react";
import {
  employeeMyTimeRequests,
  employeeMyTimeRequestsIsMobile,
} from "my-time/configs/table-defs";
import { MY_TIME_APPROVE_ALL_REQUESTS } from "my-time/graphql";
import { useMyTimeRequests } from "my-time/hooks";
import { OcCard, OcDataTable } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcButtonGroup,
  useOcTableState,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcSnackbar,
} from "oc/hooks";
import getSortedList from "oc/utils/get-sorted-list";

interface MyTimeEmployeeRequestsCardProps {
  contractCode: string;
  date: Date | string;
  label: string;
  employeeCode: string;
  examinerView: boolean;
  showApproveAllButton: boolean;
}

interface MoreButtonParams {
  action: string;
  row: any;
}

export const MyTimeEmployeeRequestsCard = ({
  contractCode,
  date,
  label,
  employeeCode,
  examinerView,
  showApproveAllButton,
}: MyTimeEmployeeRequestsCardProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();
  const classes = useStyles({ isMobile, createLeaveRequestIsEnabled: "" });
  let { contractCode: contract } = useOcCurrentUserState();

  let url = "";

  const [selectedRowId, setSelectedRowId] = useState<
    string | number | undefined
  >(undefined);

  const handleSelectRow = (row: any) => {
    if (examinerView) {
      url = `/my-time/employee-request/${employeeCode}/${contractCode}/${row._id}`;
    } else {
      url = `/my-time/show-request/${employeeCode}/${contractCode}/${row._id}`;
    }
    history.push(url);
  };

  const onSelectRow = (row: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === row?._id ? undefined : row?._id);
    } else {
      handleSelectRow(row);
    }
  };
  const [allApproveAction, setAllApproveAction] = useState(false);
  const [approveAllLoading, setApproveAllLoading] = useState(false);

  const buttonsDef = ["OC_REQUESTED_PAST", "OC_ALL"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: "MySheetMyTimesheet",
    buttonsDef,
  });

  const { data, loading, error } = useMyTimeRequests({
    contractCode,
    startDate: moment(date).startOf("month").toDate().toISOString(),
    endDate: moment(date).endOf("month").toDate().toISOString(),
    requested: selectedButton === "OC_REQUESTED_PAST" ? true : false,
    page: 1,
    pageSize: 5,
  });



  let tableId = "myTimeEmployeeRequestsTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "title",
    pageSize: 5,
  });

  const requestCounter = data?.filter((item) => item?.state === "requested");

  const { setOpenMessage } = useOcSnackbar();

  const [approveAllRequests] = useMutation(MY_TIME_APPROVE_ALL_REQUESTS, {
    onCompleted: (data) => {
      const results = data?.approveAllMyTimeRequests;
      let isOK = false;

      results.forEach((result: any) => {
        if (result.result) {
          isOK = true;
        } else {
          setOpenMessage({
            type: "error",
            message: t("OC_ALL_APPROVE_FAILED"),
          });
        }
      });
      if (isOK) {
        setOpenMessage({
          type: "success",
          message: t("MY_TIME_ALL_APPROVE_SUCEEDED"),
        });
        // Navigate back to approve requests list after successful approval
        setAllApproveAction(true);
      }
      setApproveAllLoading(false);
    },
    onError: () => {
      setOpenMessage({
        type: "error",
        message: t("MY_TIME_APPROVE_FAILED"),
      });
      setApproveAllLoading(false);
    },
  });

  const handleApproveAllRequests = () => {
    const params = { contractCode };
    setApproveAllLoading(true);
    approveAllRequests({ variables: params });
  };

  const tableDef = isMobile
    ? employeeMyTimeRequestsIsMobile
    : employeeMyTimeRequests;

  const filteredTableDef = {
    id: employeeMyTimeRequests?.id,
    columns: employeeMyTimeRequests?.columns?.filter(
      (column) =>
        !employeeMyTimeRequestsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  useEffect(() => {
    if (allApproveAction) {
      const url = `/my-time/approve-requests`;
      history.push(url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allApproveAction]);

  const disabled = contract === contractCode;

  const rows = Array.isArray(data)
    ? [...data].map((row: any) => ({
        ...row,
        period: row.startTime  // Add period field for sorting
      }))
    : [];

  rows.sort(
    getSortedList(
      tableData?.sortDirection === "desc"
        ? tableData?.sortField
        : `-${tableData?.sortField || "title"}`
    )
  );

  const list = {
    rows,
    total: rows.length,
    page: tableData?.page,
    pageSize: tableData?.pageSize || initialTableData.pageSize,
  }

  const hasRequestedItems =
    Array.isArray(list?.rows) &&
    list.rows.length > 0 &&
    list.rows.some((item) => item.state === "requested");

  return (
    <OcCard label={label} loading={loading || approveAllLoading} error={!!error}>
      <Box className={classes.root}>
        <Box
          className={
            !examinerView && !showApproveAllButton
              ? classes.leftContainerExaminerView
              : classes.leftContainer
          }
        />
        <Box
          sx={{
            display: isMobile ? "flex" : "unset",
            justifyContent: isMobile ? "center" : "unset",
          }}
        >
          <OcButtonGroup />
        </Box>
        {isMobile && <Box height="36px" />}
        <Box className={classes.rightContainer}>
          {examinerView && showApproveAllButton ? (
            <Button
              id="all-approve"
              variant="outlined"
              color="secondary"
              startIcon={<InlineIcon icon={checkAll} />}
              onClick={handleApproveAllRequests}
              className={classes.allApprove}
              disableElevation
              disabled={loading || approveAllLoading || requestCounter?.length === 0 || disabled || !hasRequestedItems}
            >
              {t("OC_ALL_APPROVE")}
            </Button>
          ) : (
            <Box>{examinerView && <Box width="180px" />}</Box>
          )}
        </Box>
      </Box>
      <Box padding="0px 0px 16px 0px">
        <OcDataTable
          tableId={tableId}
          tableData={tableData}
          setTableData={setTableData}
          tableDef={tableDef}
          data={list}
          loading={loading}
          onSelectRow={onSelectRow}
          pagination={true}
          localPaging={true}
          filteredTableDef={filteredTableDef}
          handleMoreButton={({ row }: MoreButtonParams) => handleSelectRow(row)}
          selectedRowId={selectedRowId}
        />
      </Box>
    </OcCard>
  );
};

export default MyTimeEmployeeRequestsCard;
